import { Routes } from "@/utils/routes";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";

const PROJECT_ID_KEY = "chalk-project-id";
const ENVIRONMENT_ID_KEY = "chalk-environment-id";

export interface ProjectIdPair {
  projectId: string;
  environmentId: string;
}

interface IdStorage {
  setProject(project: ProjectIdPair | null): void;
  getProject(): ProjectIdPair | null;
}

const LocalStorageIdStorage: IdStorage = {
  setProject(project: ProjectIdPair | null) {
    if (project) {
      localStorage.setItem(PROJECT_ID_KEY, project.projectId);
      localStorage.setItem(ENVIRONMENT_ID_KEY, project.environmentId);
    } else {
      localStorage.removeItem(PROJECT_ID_KEY);
      localStorage.removeItem(ENVIRONMENT_ID_KEY);
    }
  },

  getProject(): ProjectIdPair | null {
    const projectId = localStorage.getItem(PROJECT_ID_KEY);
    const environmentId = localStorage.getItem(ENVIRONMENT_ID_KEY);

    if (projectId && environmentId) {
      return {
        projectId,
        environmentId,
      };
    } else {
      return null;
    }
  },
};

const DummyIdStorage: IdStorage = {
  setProject(project: ProjectIdPair | null) {},
  getProject(): ProjectIdPair | null {
    return null;
  },
};

export const IdStorageImpl =
  typeof localStorage !== "undefined" ? LocalStorageIdStorage : DummyIdStorage;

export const useDefaultProject = () => {
  const [proj, setProj] = useState<ProjectIdPair | null>(IdStorageImpl.getProject());

  const updateProject = useCallback(
    (p: ProjectIdPair | null) => {
      IdStorageImpl.setProject(p);
      setProj(p);
    },
    [setProj],
  );

  return [proj, updateProject] as [ProjectIdPair | null, (p: ProjectIdPair | null) => void];
};

export const useDefaultProjectRedirect = () => {
  const [willRedirect, setWillRedirect] = useState(true);
  const router = useRouter();

  useEffect(() => {
    const hash = router.asPath.split("#")[1];
    const project = IdStorageImpl.getProject();
    const willRedirect = hash === "default" && project != null;
    if (willRedirect) {
      router.replace(Routes.project.environment.root(project.projectId, project.environmentId));
    } else {
      setWillRedirect(false);
    }
  }, [router]);

  return willRedirect;
};
